import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const { t, ts } = useLang();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("pwd.reset_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data);  // reconnecté automatiquement (anciennes sessions révoquées)
      setDone(true);
      toast.success(t("pwd.reset_done"));
      setTimeout(() => navigate("/compte"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-page" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO title={`${ts("pwd.reset_title")} | Mr Trophée`} description={ts("pwd.reset_sub")} path="/reinitialiser-mot-de-passe" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-3">{t("login.kicker")}</p>
          <h1 className="font-serif text-4xl mb-2">{t("pwd.reset_title")}</h1>
          {done ? (
            <div className="text-center pt-4">
              <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-5" />
              <p className="text-sm text-ivory/70" data-testid="reset-done">{t("pwd.reset_done")}</p>
            </div>
          ) : !token ? (
            <p className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 rounded-lg px-4 py-3 mt-4" data-testid="reset-no-token">
              {t("confirm.error_text")}
            </p>
          ) : (
            <>
              <p className="text-ivory/70 text-sm mb-8">{t("pwd.reset_sub")}</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">{t("pwd.reset_new")}</label>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"} name="new-password" autoComplete="new-password"
                      required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-password"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 pr-12 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} data-testid="reset-toggle-password"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 hover:text-gold transition-colors">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">{t("pwd.reset_confirm")}</label>
                  <input
                    type={showPwd ? "text" : "password"} name="new-password-confirm" autoComplete="new-password"
                    required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                  />
                </div>
                {error && <p data-testid="reset-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 rounded-lg px-4 py-3">{error}</p>}
                <button
                  type="submit" data-testid="reset-submit" disabled={loading}
                  className="w-full bg-gold text-ink font-semibold py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("pwd.reset_submit")}
                </button>
              </form>
              <p className="text-center text-sm text-ivory/70 mt-6">
                <Link to="/connexion" className="text-gold hover:underline">{t("pwd.back_login")}</Link>
              </p>
            </>
          )}
        </div>
      </FadeUp>
    </div>
  );
}
